import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import { makeGetMilestone, makeGetSortedActiveProjectMilestones } from '../selectors/milestones';
import { makeGetMilestoneTasks, makeGetCompletedTasks, makeGetOpenTasks } from '../selectors/tasks';
import { makeGetTaskGroup, makeGetSortedActiveProjectTaskGroups } from '../selectors/task-groups';
import { makeGetLoadingState, makeGetFetchedState } from '../selectors/global';

import { fetchMilestone, updateMilestone, removeMilestone, setMilestoneVisibility } from '../actions/milestones';
import { fetchTasks, archiveTasks } from '../actions/tasks';
import { fetchTaskGroups, updateTaskGroup } from '../actions/task-groups';
import { setCustomFieldValue } from '../actions/custom-fields';
import { createRecursionRule, updateRecursionRule, removeRecursionRule } from '../actions/recursion-rules';
import { duplicateMilestone } from '../api/milestones-api';

import { createAlert } from '../services/alert-service';
import { canUser } from '../services/permissions-service';
import { getMilestoneType } from '../utils/milestone';
import { getUserPreference, setUserPreference } from '../services/user-preferences-service';

import RecursionRuleContainer from '../containers/recursion-rule-container';

import CanUser from './can-user';
import DropdownElement from './dropdown-element';
import DescriptionPane from './description-pane';
import DateRangeElement from './date-range-element';
import TaskSingle from './task-single';
import CustomFields from './custom-fields';
import FeatureAvailable from './feature-available';
import CustomFieldsUpgradeNotice from './custom-fields-upgrade-notice';
import ResourceRepeatPopout from './resource-repeat-popout';
import Tooltip from './tooltip';
import ResourceRepeatText from './resource-repeat-text';
import HideFrom from './hide-from';
import SelectMilestonePopout from './select-milestone-popout';
import MultiLineInput from 'components/MultiLineInput';
import MilestoneTasks from 'components/MilestoneTasks';
import DateTime from 'components/DateTime';

import IconClose from '../icons/icon-close';
import IconLock from '../icons/icon-lock';
import IconArrowDown from 'icons/icon-arrow-down';
import IconArrowLeft from '../icons/icon-arrow-left';
import IconCustomField from '../icons/icon-custom-field';
import IconRepeat from '../icons/icon-repeat';
import IconCalendar from '../icons/icon-calendar';
import IconCalendarSimple from '../icons/icon-calendar-simple';
import IconEdit from '../icons/icon-edit';
import IconArchive from '../icons/icon-archive';
import IconNumber from '../icons/icon-number';
import IconEye from '../icons/icon-eye';
import IconExpand from '../icons/icon-expand';
import IconCollapse from '../icons/icon-collapse';

class MilestoneSingle extends PureComponent {
    static defaultProps = {
        large: true
    };

    state = {
        name: this.name,
        currentTaskId: null,
        collapsed: getUserPreference('content_panel_collapsed') || false
    }

    constructor(props) {
        super(props);

        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    handleKeyDown(event) {
        if (event.which === 27 && (event.target.nodeName !== 'INPUT' && event.target.nodeName !== 'TEXTAREA')) {
            this.close();
        }
    }

    handleNameBlur = nextName => {
        const nextProps = { name: nextName };

        if (this.props.milestone && this.props.milestone.task_group) {
            updateTaskGroup(this.props.milestone.task_group, nextProps)
        } else {
            this.update(nextProps);
        }
    }

    handleDescriptionChange = description => {
        this.update({ description });
    }

    handleDependencySelect = ({ id: dependency }) => {
        const dep = this.props.milestones.find(m => m.id === dependency);

        if (dep && new Date(dep.start) > new Date(this.props.milestone.start)) {
            createAlert('default', 'Can\'t set Milestone dependency that starts after this Milestone.');
            return;
        }

        this.update({ dependency });
    }

    handleDependencyRemove = () => {
        this.update({ dependency: null });
    }

    handleMilestoneReveal = roleIds => {
        setMilestoneVisibility(this.props.id, true, roleIds);
    }

    handleMilestoneHide = roleIds => {
        setMilestoneVisibility(this.props.id, false, roleIds);
    }

    handleActionClick = ({ id }) => {
        switch (id) {
            case 'duplicateMilestone':
                duplicateMilestone(this.props.id);
                break;

            case 'remove':
                removeMilestone(this.props.id);
                this.close();
                break;

            case 'removeAndArchive':
                removeMilestone(this.props.id);
                if (this.props.tasks.length) {
                    archiveTasks({
                        task_ids: this.props.tasks.map(task => task.id),
                        archived: true
                    });
                }
                this.close();
                break;

            default:
                return null;
        }
    }

    handleSizeToggle = () => {
        setUserPreference('content_panel_collapsed', !this.state.collapsed);

        this.setState({
            collapsed: !this.state.collapsed
        });
    }

    handleStartEndChange = ({ start, end }) => {
        this.update({
            start,
            end
        });
    }

    handleTaskClick = taskId => {
        this.setState({
            currentTaskId: taskId
        });
    }

    handleTaskClose = () => {
        this.closeTask();
    }

    handleCustomFieldChange = (id, value) => {
        setCustomFieldValue('milestone', this.props.id, id, value);
    }

    handleCustomFieldCreateClick = () => {
        this.customFieldsCtrl && this.customFieldsCtrl.create();
    }

    handleRepeatMilestoneUpdate = ({ type, trigger, cron, till }) => {
        if (this.props.milestone.rule) {
            updateRecursionRule(this.props.milestone.rule, {
                type,
                trigger,
                cron,
                till
            })

            return;
        }

        createRecursionRule({
            milestone: this.props.id,
            type,
            trigger,
            cron,
            till
        });
    }

    handleRepeatDisable = () => {
        removeRecursionRule(this.props.milestone.rule);
    }

    handleArchiveClick = () => {
        this.update({
            archived: true
        });

        this.close();
    }

    handleUnarchiveClick = () => {
        this.update({
            archived: false
        });

        this.close();
    }

    closeTask() {
        this.setState({
            currentTaskId: null
        });
    }

    getDuration(start, end) {
        return moment(end).diff(start, 'days');
    }

    update(props, preventSave) {
        return updateMilestone(this.props.id, props, preventSave);
    }

    close() {
        if (this.props.onBack) {
            this.props.onBack();
            return;
        }

        this.props.onClose && this.props.onClose();
    }

    attemptFetchMilestone(force) {
        if (
            !force && (
                !this.props.id ||
                (this.props.milestone && !(this.props.milestone.shouldRefetch && !this.props.milestone.loading))
            )
        ) {
            return;
        }

        fetchMilestone(this.props.id);
    }

    attemptFetchTaskGroups() {
        if (this.props.milestone && this.props.milestone.project && !this.props.taskGroupsFetched && !this.props.taskGroupsLoading) {
            fetchTaskGroups(this.props.milestone.project);
        }
    }

    async attemptFetchTasks() {
        if (this.props.tasksFetched || this.props.tasksLoading || !this.props.id) {
            return;
        }

        await fetchTasks({
            milestoneId: this.props.id
        }, true);
    }

    setNewTaskCtrl = ctrl => this.newTaskCtrl = ctrl;
    setCustomFieldsCtrl = ctrl => this.customFieldsCtrl = ctrl;
    setDateRangeCtrl = ctrl => this.dateRangeCtrl = ctrl;

    getMilestoneName(milestone) {
        if (milestone) {
            if (milestone.task_group) {
                const taskGroup = this.props.taskGroups.find(taskGroup => taskGroup.id === milestone.task_group);
                if (taskGroup && taskGroup.name) {
                    return taskGroup.name;
                }
            } else {
                if (milestone.name) {
                    return milestone.name;
                }
            }
        }

        return '';
    }

    get name() {
        return this.getMilestoneName(this.props.milestone);
    }

    get readableType() {
        switch (this.type) {
            case 'overdue':
                return 'Overdue';

            case 'completed':
                return 'Completed';

            case 'default':
                return 'Not Started';

            default:
                return 'In Progress';
        }
    }

    get duration() {
        return this.getDuration(this.props.milestone.start, this.props.milestone.end) + 1;
    }

    get type() {
        return getMilestoneType(this.props.milestone);
    }

    get actions() {
        const actions = [{
            text: 'Duplicate Milestone',
            id: 'duplicateMilestone'
        }];

        if (!this.props.milestone.task_group) {
            actions.push({
                text: 'Remove Milestone',
                id: 'remove',
                prompt: {
                    title: 'DELETE MILESTONE',
                    text: 'Do you really want to delete this milestone?',
                    confirmText: 'Delete',
                    confirmClassName: 'btn-caution'
                }
            });
        }

        actions.push({
            text: 'Remove Milestone and Archive Tied Tasks',
            id: 'removeAndArchive',
            prompt: {
                title: 'DELETE MILESTONE',
                text: 'Do you really want to delete this milestone and archive tasks?',
                confirmText: 'Delete',
                confirmClassName: 'btn-caution'
            }
        });

        return actions;
    }

    get large() {
        return this.props.large && !this.state.collapsed;
    }

    get element() {
        const { id, milestone, taskGroups, openTasks, completedTasks } = this.props;

        if (!milestone) {
            return null;
        }

        if (!('name' in milestone) || milestone.error) {
            return null;
        }


        const tasksElement = <MilestoneTasks id={id} milestone={milestone} taskGroups={taskGroups} openTasks={openTasks} completedTasks={completedTasks} onTaskClick={this.handleTaskClick} />

        return (
            <div className="content-panel">
                <div className="content-panel-main">
                    <div className="content-panel-head">
                        <div className="head-main">
                            <div className="content-panel-head-top">
                                <div className="content-panel-head-utils">
                                    {
                                        this.props.onBack ? (
                                            <button className="content-panel-control btn content-panel-back" onClick={this.props.onBack}><IconArrowLeft size="20px" /></button>
                                        ) : null
                                    }

                                    <span className={'tag-pill ' + this.type}>{this.readableType}</span>
                                </div>

                                <div className="content-panel-controls">
                                    <div className="content-panel-simple-actions">
                                        <CanUser type="editMilestone" customList={[milestone.created_by]}>
                                            {
                                                milestone.archived ? (
                                                    <Tooltip tooltip="Unarchive milestone">
                                                        <div className="content-panel-simple-action" onClick={this.handleUnarchiveClick}>
                                                            <div className="content-panel-simple-action-inner">
                                                                <IconArchive />
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip tooltip="Archive milestone">
                                                        <div className="content-panel-simple-action" onClick={this.handleArchiveClick}>
                                                            <div className="content-panel-simple-action-inner">
                                                                <IconArchive />
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                )
                                            }
                                        </CanUser>
                                    </div>

                                    <CanUser type="removeMilestone">
                                        <div className="content-panel-control">
                                            <DropdownElement
                                                items={this.actions}
                                                defaultXPosition="left"
                                                xAlign="right"
                                                onItemClick={this.handleActionClick}
                                            >
                                                <span className="content-panel-actions-trigger" onClick={this.handleActionsTriggerClick}><i className="material-icons">more_horiz</i></span>
                                            </DropdownElement>
                                        </div>
                                    </CanUser>

                                    {
                                        this.props.large && !this.props.inside ? (
                                            <button className="content-panel-control btn content-panel-size-toggle" onClick={this.handleSizeToggle}>
                                                {
                                                    this.state.collapsed ? (
                                                        <IconExpand />
                                                    ) : (
                                                        <IconCollapse />
                                                    )
                                                }
                                            </button>
                                        ) : null
                                    }

                                    {
                                        this.props.onClose ? (
                                            <button className="content-panel-control btn content-panel-close" onClick={this.props.onClose}><IconClose /></button>
                                        ) : null
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="content-panel-main-inner">
                        <div className="content-panel-side">
                            <div className="content-panel-title">
                                <MultiLineInput
                                    className="content-panel-field-input"
                                    placeholder="Add milestone name…"
                                    canEdit={canUser('editMilestone', [milestone.created_by])}
                                    value={this.name}
                                    onBlur={this.handleNameBlur}
                                />
                            </div>

                            <div className="content-panel-scrollable nice-scroll">
                                <div className="content-panel-block">
                                    <DescriptionPane
                                        key={this.props.id}
                                        editable={canUser('editMilestone')}
                                        placeholder="Add milestone description"
                                        content={this.props.milestone.description || ''}
                                        onChange={this.handleDescriptionChange}
                                    />
                                </div>

                                <div className="content-panel-block">
                                    <div className="content-panel-utils">
                                        <div className="control half-width">
                                            <div className="control-inner">
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconCalendar />
                                                    </span>

                                                    <span className="control-text">
                                                        Start & End Date
                                                    </span>
                                                </div>

                                                <CanUser type="editMilestone">
                                                    <DateRangeElement
                                                        title="Select the start and end dates:"
                                                        value={this.props.milestone}
                                                        onChange={this.handleStartEndChange}
                                                        provideController={this.setDateRangeCtrl}
                                                    >
                                                        <div className="control-value">
                                                            <DateTime format="date">{this.props.milestone.start}</DateTime> - <DateTime format="date">{this.props.milestone.end}</DateTime>

                                                            <span className="control-value-edit">
                                                                <IconEdit />
                                                            </span>
                                                        </div>
                                                    </DateRangeElement>
                                                </CanUser>

                                                <CanUser type="editMilestone" reverse={true}>
                                                    <div className="control-value">
                                                        <DateTime format="date">{this.props.milestone.start}</DateTime> - <DateTime format="date">{this.props.milestone.end}</DateTime>
                                                    </div>
                                                </CanUser>
                                            </div>
                                        </div>

                                        <div className="control half-width">
                                            <div className="control-inner">
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconCalendarSimple />
                                                    </span>

                                                    <span className="control-text">
                                                         Duration
                                                    </span>
                                                </div>

                                                <div className="control-value">
                                                    {this.duration} day{this.duration === 1 ? '' : 's'}
                                                </div>
                                            </div>
                                        </div>

                                        {
                                            !this.props.public ? (
                                                <div className="control half-width">
                                                    <div className="control-inner">
                                                        <div className="control-label">
                                                            <span className="control-icon">
                                                                <IconRepeat />
                                                            </span>

                                                            <span className="control-text">
                                                                Recurring
                                                            </span>
                                                        </div>

                                                        <div className="control-value">
                                                            {
                                                                this.props.milestone.rule ? (
                                                                    <RecursionRuleContainer id={this.props.milestone.rule}>
                                                                        <ResourceRepeatText />
                                                                    </RecursionRuleContainer>
                                                                ) : (
                                                                    <span className="action-text">Set…</span>
                                                                )
                                                            }
                                                        </div>

                                                        <CanUser type="editMilestone" customList={[milestone.created_by]}>
                                                            <RecursionRuleContainer id={this.props.milestone.rule}>
                                                                <ResourceRepeatPopout
                                                                    key={this.props.id}
                                                                    title="REPEAT THIS MILESTONE"
                                                                    projectId={this.props.milestone.project}
                                                                    xAlign="right"
                                                                    defaultXPosition="left"
                                                                    rule={this.props.milestone.rule}
                                                                    types={['date']}
                                                                    onChange={this.handleRepeatMilestoneUpdate}
                                                                    onDisable={this.handleRepeatDisable}
                                                                >
                                                                    <div className="popout-toggle" />
                                                                </ResourceRepeatPopout>
                                                            </RecursionRuleContainer>
                                                        </CanUser>
                                                    </div>
                                                </div>
                                            ) : null
                                        }

                                        <div className="control half-width">
                                            <div className="control-inner">
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconNumber />
                                                    </span>

                                                    <span className="control-text">
                                                         Story Points
                                                    </span>
                                                </div>

                                                <div className="control-value">
                                                    { milestone.statistic.completed_story_points }/{ milestone.statistic.total_story_points }
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                <div className="content-panel-meta-block">
                                    <CanUser type="editMilestone">
                                        <div className="meta-item meta-item-clickable">
                                            <SelectMilestonePopout
                                                title="SELECT DEPENDENCY"
                                                projectId={milestone.project}
                                                selected={milestone.dependency}
                                                onSelect={this.handleDependencySelect}
                                                onRemove={this.handleDependencyRemove}
                                                removeCopy="Remove dependency"
                                                excluded={[this.props.id, ...this.props.milestones.filter(milestone => milestone.dependency === this.props.id).map(m => m.id)]}
                                                xAlign="left"
                                                defaultXPosition="right"
                                            >
                                                <div className="meta-item-inner">
                                                    <div className="meta-item-icon">
                                                        <IconLock />
                                                    </div>

                                                    <div className="meta-item-text">
                                                        {
                                                            this.props.dependency ? this.getMilestoneName(this.props.dependency) : 'Set a Milestone Dependency'
                                                        }
                                                    </div>

                                                    <div className="meta-item-action">
                                                        <IconArrowDown size="11px" />
                                                    </div>
                                                </div>
                                            </SelectMilestonePopout>
                                        </div>
                                    </CanUser>

                                    <CanUser type="editMilestone" reverse={true}>
                                        <div className="meta-item">
                                            <div className="meta-item-inner">
                                                <div className="meta-item-icon">
                                                    <IconLock />
                                                </div>

                                                <div className="meta-item-text">
                                                    {
                                                        this.props.dependency ? this.getMilestoneName(this.props.dependency) : 'No Milestone Dependency'
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </CanUser>

                                    <CanUser type="editMilestone">
                                        <div className={'meta-item meta-item-clickable' + ((this.props.taskGroup || this.props.milestone)?.hidden?.length ? ' active' : '')}>
                                            <HideFrom
                                                xAlign="right"
                                                defaultXPosition="left"
                                                hidden={(this.props.taskGroup || this.props.milestone)?.hidden}
                                                creator={this.props.milestone?.created_by}
                                                onHide={this.handleMilestoneHide}
                                                onReveal={this.handleMilestoneReveal}
                                            >
                                                <div className="meta-item-inner">
                                                    <div className="meta-item-icon">
                                                        <IconEye size="14px" />
                                                    </div>

                                                    <div className="meta-item-text">
                                                        Hide Milestone from
                                                    </div>

                                                    <div className="meta-item-action">
                                                        <IconArrowDown />
                                                    </div>
                                                </div>
                                            </HideFrom>
                                        </div>
                                    </CanUser>
                                </div>

                                <div className="content-panel-body">
                                    <div className="content-panel-body-main">
                                        <div className="content-panel-section hide-on-large">
                                            { tasksElement }
                                        </div>

                                        {
                                            !this.props.public ? (
                                                <div className="content-panel-section">
                                                    <div className="section-head">
                                                        <h4>
                                                            <span className="title-icon"><IconCustomField /></span>

                                                            <span className="text">Custom Fields</span>

                                                            <FeatureAvailable type="customFields">
                                                                <CanUser type="editCustomFields">
                                                                    <button className="btn btn-circle btn-xxsmall btn-simple btn-icon btn-colored" onClick={this.handleCustomFieldCreateClick}>+</button>
                                                                </CanUser>
                                                            </FeatureAvailable>
                                                        </h4>
                                                    </div>
                                                    <div className="section-body">
                                                        <FeatureAvailable type="customFields">
                                                            <CustomFields
                                                                key={this.props.id}
                                                                projectId={this.props.milestone.project}
                                                                type="row-single"
                                                                association="milestone"
                                                                associationId={this.props.id}
                                                                values={this.props.milestone.fields}
                                                                onChange={this.handleCustomFieldChange}
                                                                provideController={this.setCustomFieldsCtrl}
                                                            />
                                                        </FeatureAvailable>

                                                        <FeatureAvailable type="customFields" reverse={true}>
                                                            <CustomFieldsUpgradeNotice />
                                                        </FeatureAvailable>
                                                    </div>
                                                </div>
                                            ) : null
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {
                            this.large ? (
                                <div className="content-panel-side">
                                    <div className="content-panel-scrollable nice-scroll">
                                        <div className="content-panel-body">
                                            <div className="content-panel-body-main">
                                                <div className="content-panel-section">
                                                    { tasksElement }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        }
                    </div>
                </div>
            </div>
        )
    }

    componentDidMount() {
        if (this.props.milestone && (this.props.milestone.error || this.props.milestone.removed)) {
            this.close();
            return;
        }

        if (this.props.id) {
            this.attemptFetchMilestone(true);
            this.attemptFetchTaskGroups();
            this.attemptFetchTasks();
        }

        window.addEventListener('keydown', this.handleKeyDown);

        if (this.props.datepickerOnInit) {
            this.dateRangeCtrl && this.dateRangeCtrl.show();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    componentDidUpdate(prevProps) {
        if (this.props.milestone && (this.props.milestone.error || this.props.milestone.removed)) {
            this.close();
            return;
        }

        if (this.props.id !== prevProps.id) {
            this.closeTask();

            if (this.props.id) {
                this.attemptFetchMilestone(true);
            } else {
                this.attemptFetchMilestone();
            }

            this.attemptFetchTaskGroups();
            this.attemptFetchTasks();
        }
    }

    render() {
        return (
            <div className={'content-panel-holder' + (this.props.id ? ' visible' : '') + (this.props.inside ? ' inside' : '') + (this.state.currentTaskId ? ' subview-visible' : '') + (this.large ? ' large' : '')}>
                { this.element }

                {
                    this.props.id ? (
                        <TaskSingle
                            id={this.state.currentTaskId}
                            inside={true}
                            large={this.large}
                            noRedirect={true}
                            shouldChangeUrl={false}
                            public={this.props.public}
                            onBack={this.handleTaskClose}
                            onClose={this.props.onClose}
                        />
                    ) : null
                }
            </div>
        )
    }
}

const makeMapStateToProps = () => {
    const getMilestone = makeGetMilestone();
    const getTaskGroup = makeGetTaskGroup();
    const getSortedActiveProjectMilestones = makeGetSortedActiveProjectMilestones();
    const getSortedActiveProjectTaskGroups = makeGetSortedActiveProjectTaskGroups();
    const getTaskGroupsLoading = makeGetLoadingState();
    const getTaskGroupsFetched = makeGetFetchedState();
    const getMilestoneTasks = makeGetMilestoneTasks();
    const getTasksFetched = makeGetFetchedState();
    const getTasksLoading = makeGetLoadingState();
    const getOpenTasks = makeGetOpenTasks();
    const getCompletedTasks = makeGetCompletedTasks();

    return ({ milestones, tasks, taskGroups }, ownProps) => {
        const milestone = getMilestone(milestones.items, ownProps.id);
        const projectId = milestone && milestone.project;
        const milestoneTasks = getMilestoneTasks(tasks.items, ownProps.id);

        return {
            milestone,
            taskGroup: milestone && milestone.task_group && getTaskGroup(taskGroups.items, milestone.task_group),
            dependency: milestone && milestone.dependency && getMilestone(milestones.items, milestone.dependency),
            milestones: (projectId && getSortedActiveProjectMilestones(milestones.items, milestone.project)) || [],
            tasks: milestoneTasks,
            openTasks: getOpenTasks(milestoneTasks),
            completedTasks: getCompletedTasks(milestoneTasks),
            tasksLoading: getTasksLoading(tasks.fetchInfo, 'milestone', ownProps.id),
            tasksFetched: getTasksFetched(tasks.fetchInfo, 'milestone', ownProps.id),
            taskGroups: (projectId && getSortedActiveProjectTaskGroups(taskGroups.items, projectId)) || [],
            taskGroupsLoading: projectId && getTaskGroupsLoading(taskGroups.fetchInfo, 'project', projectId),
            taskGroupsFetched: projectId && getTaskGroupsFetched(taskGroups.fetchInfo, 'project', projectId)
        }
    }
}

export default connect(makeMapStateToProps)(MilestoneSingle);
